import Drawer from '@mui/material/Drawer'
import { createNote, deleteNote, getNoteById, updateNote } from 'api/notes'
import { shareTeamContent } from 'api/teams'
import { defaultDrawerSX } from 'const'
import { useShareRecipients } from 'features/Share/hooks/useShareRecipients'
import { UIPencilImg } from 'features/UI'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'
import { routes } from 'routes/routes'
import { useActions } from 'store/hooks'
import { Note } from 'types'
import { useAuthContext } from '../../context/AuthProvider'
import NotificationSys from '../../NotificationSystem'
import DrawerContainer from '../common/DrawerContainer'
import DrawerHeader from '../common/DrawerHeader'
import NoteEditForm from './NoteEditForm'
import { NoteFormValues, NoteType } from './noteForm'

interface Props {
  isOpen: boolean
  id?: number
  handleClose: () => void
  handleOpen: (id?: number) => void
}

const EditNoteDrawer = (props: Props) => {
  const { isOpen, id, handleClose, handleOpen } = props

  const [data, setData] = useState<Note | undefined>(undefined)
  const [noteType, setNoteType] = useState<NoteType>(NoteType.PERSONAL)

  const { user } = useAuthContext()
  const { getTimelineById, getSharedTimelineSilent } = useActions()
  const { teamMembersOptions, activeTeamId, resolveUserIds } = useShareRecipients()
  const location = useLocation()

  const isCreate = !id

  useEffect(() => {
    if (!isOpen) {
      setData(undefined)
      setNoteType(NoteType.PERSONAL)
    }
  }, [isOpen])

  const query = useQuery(['getNoteById', { id }], () => getNoteById(id ?? -1), {
    enabled: isOpen && !!id,
    select: (req) => req.data,
    onSuccess: setData,
    cacheTime: 0,
    staleTime: 0,
  })

  const createMutation = useMutation(createNote)
  const shareMutation = useMutation(shareTeamContent)

  const updateMutation = useMutation(updateNote, {
    onSuccess: (response) => {
      setData(response.data)
      getTimelineById({})
      NotificationSys.showSuccess(`Note successfully updated`)
      handleClose()
    },
  })

  const removeMutation = useMutation(deleteNote, {
    onSuccess: () => {
      getTimelineById({})
      NotificationSys.showSuccess(`Note successfully removed`)
      handleClose()
    },
  })

  const onRemoveNote = useCallback(() => {
    if (id) {
      removeMutation.mutate(id)
    }
  }, [id, removeMutation])

  // Create a Shared Note: create the note first, then share it with the selected
  // recipients. If sharing fails, keep the note (it is already on the timeline) and
  // warn the user so they can share it from the view drawer.
  const shareCreatedNote = useCallback(
    async (noteId: number, values: NoteFormValues) => {
      if (!activeTeamId) {
        NotificationSys.showWarning('Please select a team to share the note')
        return
      }

      try {
        await shareMutation.mutateAsync({
          userIds: resolveUserIds(values.type, values.members),
          careTeamId: activeTeamId,
          comment: values.comments || '',
          entityId: noteId,
          type: 'note',
        })
        NotificationSys.showSuccess(`Shared note successfully created`)

        // Keep the Shared View in sync when the author is currently on that page.
        if (location.pathname.startsWith(routes.shared) && user?.id) {
          getSharedTimelineSilent({ userId: user.id, teamId: activeTeamId })
        }
      } catch (e) {
        NotificationSys.showWarning(
          'Note was created, but sharing failed. You can share it from the timeline.',
        )
      }
    },
    [
      activeTeamId,
      resolveUserIds,
      shareMutation,
      location.pathname,
      user?.id,
      getSharedTimelineSilent,
    ],
  )

  const onSubmitHandler = async (values: NoteFormValues) => {
    if (data) {
      updateMutation.mutate({
        subject: values.subject,
        body: values.body,
        link: values.link,
        id: data.id,
        userId: data.userId,
        createdAt: data?.createdAt,
        authorId: data.authorId,
        authorName: data.authorName,
      })
      return
    }

    try {
      const response = await createMutation.mutateAsync({
        subject: values.subject,
        body: values.body,
        link: values.link,
        userId: user?.id ?? -1,
      })

      if (values.noteType === NoteType.SHARED) {
        await shareCreatedNote(response.data.id, values)
      } else {
        NotificationSys.showSuccess(`Note successfully created`)
      }

      getTimelineById({})
      handleClose()
    } catch (e) {
      NotificationSys.showWarning('An error occurred while creating the note')
    }
  }

  const headerTitle = useMemo(() => {
    if (!isCreate) {
      return 'Edit Note'
    }
    return noteType === NoteType.SHARED ? 'Create a Note' : 'Note to add to Timeline'
  }, [isCreate, noteType])

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose} sx={defaultDrawerSX}>
      <DrawerContainer
        isOpen={isOpen}
        hasData={!query.isError}
        loading={
          query.isLoading ||
          createMutation.isLoading ||
          shareMutation.isLoading ||
          updateMutation.isLoading ||
          removeMutation.isLoading
        }
        handleClose={handleClose}
      >
        <DrawerHeader handleClose={handleClose} borderEnable={true}>
          <UIPencilImg />
          <span>{headerTitle}</span>
        </DrawerHeader>
        <NoteEditForm
          values={data}
          onSubmitHandler={onSubmitHandler}
          removeDisabled={!id}
          onRemove={onRemoveNote}
          isShareable={isCreate}
          shareMemberOptions={teamMembersOptions}
          onNoteTypeChange={setNoteType}
        />
      </DrawerContainer>
    </Drawer>
  )
}

export default EditNoteDrawer
